import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Box,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import type { ReportBlock, ReportDocument, ReportMeta } from '../../lib/pdf/reportModel';
import { writeReportEdit } from '../../lib/pdf/reportEdits';
import { useNotifications } from '../../context/NotificationContext';
import { AppDialogTitle } from './AppDialogTitle';

interface ReportEditDialogProps {
  open: boolean;
  onClose: () => void;
  meta: ReportMeta;
  /** Startdokument (bereits ggf. mit gespeicherten Bearbeitungen zusammengeführt). */
  initialDocument: ReportDocument;
  /** Storage-Key für die Persistenz dieses Reports (Projekt + Seite). */
  editKey: string;
  onExportPdf: (doc: ReportDocument) => Promise<void> | void;
  onExportDocx: (doc: ReportDocument) => Promise<void> | void;
}

/** Tiefe Kopie — ReportDocument enthält nur Plain-Data (keine Funktionen/Dates), JSON reicht. */
function cloneDocument(doc: ReportDocument): ReportDocument {
  return JSON.parse(JSON.stringify(doc)) as ReportDocument;
}

/**
 * Generischer Editier-Layer für Report-Inhalte vor dem Export (P-20).
 *
 * Arbeitet direkt auf dem deklarativen {@link ReportDocument}-Modell — funktioniert
 * dadurch für JEDEN Report, der bereits einen PDF-Export deklariert (13 Report-Seiten
 * + zukünftige), ohne dass jede Seite ihren eigenen Editor bauen müsste. Die
 * STRUKTUR (Abschnitte, Spalten, Zeilenzahl) bleibt fest — bearbeitbar sind die
 * Inhalte selbst (Texte, Key-Value-Werte, Tabellenzellen), analog zum Muster aus
 * StatusReportTab, aber ohne dessen seiten-spezifische Sonderlogik.
 */
export function ReportEditDialog({
  open,
  onClose,
  meta,
  initialDocument,
  editKey,
  onExportPdf,
  onExportDocx,
}: ReportEditDialogProps) {
  const { t } = useTranslation('common');
  const { notify } = useNotifications();
  // Einmaliger Init aus dem Startdokument beim Mount reicht — die Dialog-Komponente
  // wird von PdfExportButton nur gemountet, solange sie offen ist (conditional render),
  // ein erneutes Öffnen erzeugt also automatisch eine frische Instanz.
  const [doc, setDoc] = useState<ReportDocument>(() => cloneDocument(initialDocument));
  const [busy, setBusy] = useState<'pdf' | 'docx' | null>(null);

  const updateBlock = (sectionIdx: number, blockIdx: number, updater: (b: ReportBlock) => ReportBlock) => {
    setDoc((prev) => {
      const next = cloneDocument(prev);
      const section = next.sections[sectionIdx];
      if (!section) return prev;
      const block = section.blocks[blockIdx];
      if (!block) return prev;
      section.blocks[blockIdx] = updater(block);
      return next;
    });
  };

  const handleSave = () => {
    writeReportEdit(editKey, doc);
    notify(t('report.edit.saved', 'Bearbeitung gespeichert'), 'success');
  };

  const handleExport = async (format: 'pdf' | 'docx') => {
    setBusy(format);
    try {
      writeReportEdit(editKey, doc);
      if (format === 'pdf') await onExportPdf(doc);
      else await onExportDocx(doc);
      notify(
        format === 'pdf'
          ? t('report.edit.exportedPdf', 'PDF erzeugt')
          : t('report.edit.exportedDocx', 'Word-Dokument erzeugt'),
        'success',
      );
      onClose();
    } catch (err) {
      console.error('[ReportEditDialog] Export fehlgeschlagen:', err);
      notify(t('report.edit.exportError', 'Export fehlgeschlagen'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <AppDialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" component="span">
            {t('report.edit.title', 'Report bearbeiten')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--pm-fg-subtle)' }}>
            {meta.pageTitle} · {meta.projectName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={t('actions.close', 'Schließen')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </AppDialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {doc.sections.length === 0 && (
            <Typography variant="body2" sx={{ color: 'var(--pm-fg-subtle)' }}>
              {t('report.edit.empty', 'Für diesen Report liegen noch keine Inhalte vor.')}
            </Typography>
          )}
          {doc.sections.map((section, sIdx) => (
            <Box key={sIdx}>
              {section.title && (
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--rittec-petrol)', mb: 1 }}>
                  {section.title}
                </Typography>
              )}
              <Stack spacing={2}>
                {section.blocks.map((block, bIdx) => {
                  if (block.kind === 'keyValues') {
                    return (
                      <Stack key={bIdx} spacing={1.25}>
                        {block.items.map((item, iIdx) => (
                          <TextField
                            key={iIdx}
                            label={item.label}
                            value={item.value}
                            size="small"
                            fullWidth
                            onChange={(e) =>
                              updateBlock(sIdx, bIdx, (b) => {
                                if (b.kind !== 'keyValues') return b;
                                const items = b.items.map((it, k) => (k === iIdx ? { ...it, value: e.target.value } : it));
                                return { ...b, items };
                              })
                            }
                          />
                        ))}
                      </Stack>
                    );
                  }
                  if (block.kind === 'text') {
                    return (
                      <TextField
                        key={bIdx}
                        label={block.label}
                        value={block.value}
                        multiline
                        minRows={3}
                        fullWidth
                        onChange={(e) =>
                          updateBlock(sIdx, bIdx, (b) => (b.kind === 'text' ? { ...b, value: e.target.value } : b))
                        }
                      />
                    );
                  }
                  if (block.kind === 'note') {
                    return (
                      <TextField
                        key={bIdx}
                        label={t('report.edit.note', 'Hinweis')}
                        value={block.value}
                        fullWidth
                        size="small"
                        onChange={(e) =>
                          updateBlock(sIdx, bIdx, (b) => (b.kind === 'note' ? { ...b, value: e.target.value } : b))
                        }
                      />
                    );
                  }
                  // table
                  return (
                    <Box key={bIdx} sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {block.columns.map((col, cIdx) => (
                              <TableCell key={cIdx} sx={{ color: 'var(--pm-fg-subtle)', fontWeight: 600 }}>
                                {col.header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {block.rows.map((row, rIdx) => (
                            <TableRow key={rIdx}>
                              {row.map((cell, cIdx) => (
                                <TableCell key={cIdx} sx={{ minWidth: 100 }}>
                                  <TextField
                                    value={cell}
                                    variant="standard"
                                    fullWidth
                                    onChange={(e) =>
                                      updateBlock(sIdx, bIdx, (b) => {
                                        if (b.kind !== 'table') return b;
                                        const rows = b.rows.map((r, rk) =>
                                          rk === rIdx ? r.map((c, ck) => (ck === cIdx ? e.target.value : c)) : r,
                                        );
                                        return { ...b, rows };
                                      })
                                    }
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                          {block.rows.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={block.columns.length} sx={{ color: 'var(--pm-fg-subtle)', fontStyle: 'italic' }}>
                                {t('report.edit.noRows', 'Keine Zeilen vorhanden.')}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button startIcon={<SaveIcon />} onClick={handleSave} disabled={busy != null}>
          {t('report.edit.save', 'Bearbeitung speichern')}
        </Button>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('report.edit.exportDocxHint', 'Bearbeitete Fassung als Word-Dokument herunterladen')}>
            <span>
              <Button
                variant="outlined"
                startIcon={busy === 'docx' ? <CircularProgress size={16} /> : <DescriptionIcon />}
                disabled={busy != null}
                onClick={() => void handleExport('docx')}
              >
                {t('report.edit.exportDocx', 'Als Word exportieren')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t('report.edit.exportPdfHint', 'Bearbeitete Fassung als PDF herunterladen')}>
            <span>
              <Button
                variant="contained"
                startIcon={busy === 'pdf' ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                disabled={busy != null}
                onClick={() => void handleExport('pdf')}
                data-testid="report-edit-export-pdf"
              >
                {t('report.edit.exportPdf', 'Als PDF exportieren')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
